import React, { useContext, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  Paper,
  Tooltip, Grid,
} from "@material-ui/core";
import { DashboardContext } from "../../context/DashboardContext";
import _ from "lodash";
import TableContextRenderer from "./TableContentRenderer";
import CommonUtils from "../../utils/commonUtils";
import DashboardMode from "../../constants/DashboardMode";
import { ReactComponent as Info } from "../../assets/icons/information.svg";
import "./table.scss";
import { UserContext } from "../../context/UserContext";

const initialState = {
  jobField: { label: "Job ID", type: "jobId", fields: ["jobId"] },
  requestField: {
    label: "Request Id",
    type: "requestId",
    fields: ["requestSummary.requestId"],
  },
};

const JobTable = ({ headers, dataChanged }) => {
  const location = useLocation();
  const pageNumber = new URLSearchParams(location.search).get("pageNum");
  const {
    loading,
    jobs,
    totalPages,
    currentPage,
    rowsPerPage,
    _setSort,
    _getSort,
    _setCurrentPage,
    _setRowsPerPage,
    dashboardView,
    showLoading,
  } = useContext(DashboardContext);
  const { userRole } = useContext(UserContext);
  const tableRef = useRef(null);
  const history = useHistory();
  useEffect(() => {
    // Need to improve the below logic
    const lastViewedJobId = localStorage.getItem("lastViewedJobId");
    if (lastViewedJobId) {
      const element = document.getElementById(lastViewedJobId);
      if (element) {
        CommonUtils.scrollToTarget(element);
        element.classList.add('highlighted');
        setTimeout(() => {
          element.classList.remove('highlighted');
        }, 4000);
        localStorage.setItem("lastViewedJobId", null);
      }
    }
  }, [jobs]);

  useEffect(() => {
    const pageNum = parseInt(pageNumber, 10) || 1;
    if (pageNum !== currentPage) {
      showLoading();
      _setCurrentPage(pageNum);
      //Scroll to first row on page change
      tableRef && tableRef.current && tableRef.current.scrollIntoView();
    }
    // eslint-disable-next-line
  }, [pageNumber]);

  const handleChangePage = (event, newPage) => {
    showLoading();
    const activePage = parseInt(newPage, 10) + 1;
    _setCurrentPage(activePage);
    history.push(`/?pageNum=${activePage}`);
    //Scroll to first row on page change
    tableRef.current.scrollIntoView();
  };

  const handleChangeRowsPerPage = (event) => {
    showLoading();
    _setRowsPerPage(parseInt(event.target.value, 10));
    _setCurrentPage(1);
    history.push('/?pageNum=1');
  };

  //sonar code smell fix,changed headers to headersArray because headers is already declared in out of scope
  const requestHeaders = (headersArray) => {
    const mapHeaders = _.map(headersArray, (column) => {
      if (column.label === "Job ID") {
        return initialState.requestField;
      }
      if (column.label === "Assigned to") {
        return null;
      }
      return column;
    });
    return _.filter(mapHeaders, (column) => {
      return !_.isEmpty(column);
    });
  };

  const showInfoIcon = (column) => {
    if (_.includes(['cr_api', 'adminwithreadaccess'], userRole) && _.has(column, 'icon')) {
      return false;
    }
    return _.has(column, "title")
  }

  if (dashboardView === DashboardMode.REQUESTS) {
    return null;
  }
  
  return (
    <>
      <Grid
        item
        xs={12}
        className="dashboardGrid">
        {loading ? null : !_.isEmpty(jobs) ? null
          : (<div className="tableInfoDiv">
            <span className="tableInfoSpan">
              No Jobs found
            </span>
          </div>)
        }
        <TableContainer component={Paper} className="dashboardTableContainer">
          {_.isEmpty(jobs) ? null : (
            <Table ref={tableRef} id="job_table" stickyHeader aria-label="sticky table">
              <TableHead
                id={"job_head"}
                className="tableHead">
                <TableRow key={"job_header_row"}>
                  {/* Need to change below condition if no jobs found in request */}
                  {_.map(dashboardView === DashboardMode.JOBS ? headers : requestHeaders(headers), (column, index) => (
                    <TableCell
                      key={"job_header_" + index}
                      className="dashboardTableHeader"
                      style={{
                        width: _.get(column, "width", "8%")
                      }}>
                      <TableSortLabel
                        className="tableSort"
                        active={_.isEqual(_.keys(_getSort())[0], _.get(column, "value"))}
                        hidden={_.isEmpty(column.label)}
                        direction={_.get(_getSort(), _.keys(_getSort())[0]) === "asc" ? "asc" : "desc"}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          let direction = "";
                          if (_.isEqual(_.keys(_getSort())[0], _.get(column, "value"))) {
                            direction = _.get(_getSort(), _.keys(_getSort())[0]) === "asc" ? "desc" : "asc";
                          } else {
                            direction = "asc";
                          }
                          let obj = {};
                          obj[_.get(column, "value")] = direction;
                          _setSort(obj);
                          showLoading();
                        }}>
                        { showInfoIcon(column) ? (
                          <span>
                            <span style={{ width: "min-content" }}>{column.label}</span>
                            <Tooltip
                              title={column.title}
                              interactive
                              placement="top"
                              arrow
                              classes={{ tooltip: "tableTooltip" }}>
                              <Info className="tableHeaderIcon" />
                            </Tooltip>
                          </span>
                        ) : (
                          <span>{column.label}</span>
                        )}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody
                className="tableBody"
                id={"table_container"}>
                <TableContextRenderer
                  dataChanged={dataChanged}
                  showJobs={dashboardView === DashboardMode.JOBS}
                  jobs={jobs}
                  requests={null}
                  headers={dashboardView === DashboardMode.JOBS ? headers : requestHeaders(headers)}
                  order={_.get(_getSort(), _.keys(_getSort())[0])}
                  field={_.keys(_getSort())[0]}
                />
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Grid>
      {_.isEmpty(jobs) ? null : (
        <Grid
          item
          xs={12}>
          <TablePagination
            component="div"
            className="tablePagination"
            classes={{
              toolbar: "tablePaginationToolbar"
            }}
            id={"table_pagination"}
            count={totalPages}
            page={currentPage - 1}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            nextIconButtonText="Next Page"
            backIconButtonText="Previous Page"
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Grid>
      )}
    </>
  );
};

export default JobTable;
